import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, IconButton, Typography, Slider } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MicIcon from '@mui/icons-material/Mic';
import MessageBase from './MessageBase';

// Cache global para archivos de audio ya cargados
const globalAudioCache = new Map();

const VoiceMessage = ({ text, sender, timestamp, status, media_url, duration: initialDuration, id }) => {
    // Identificador único para el mensaje de voz
    const audioId = id || `voice-${media_url?.substring(media_url?.lastIndexOf('/') + 1) || Date.now()}`;
    
    // Verificar inmediatamente si el audio ya está en caché
    const isAudioCached = useMemo(() => globalAudioCache.has(media_url), [media_url]);
    
    // Estados para controlar la reproducción
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(initialDuration || 0);
    const [waveform, setWaveform] = useState([]);
    const [loading, setLoading] = useState(!isAudioCached);
    
    // Referencias
    const audioRef = useRef(null);
    const originalUrlRef = useRef(media_url);
    
    // Efecto para generar waveform (solo una vez por mensaje)
    useEffect(() => {
        // Si ya existe un waveform para esta URL, no generamos uno nuevo
        if (waveform.length > 0) return;
        
        // Crea un array de 40 valores aleatorios entre 10 y 100 para simular un waveform
        const generateRandomWaveform = () => {
            const values = [];
            const seed = audioId.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
            
            // Usar el ID como semilla para generar valores consistentes
            const pseudoRandom = (min, max, seed, index) => {
                const val = Math.sin(seed * (index + 1)) * 10000;
                return Math.floor((val - Math.floor(val)) * (max - min)) + min;
            };
            
            for (let i = 0; i < 40; i++) {
                values.push(pseudoRandom(10, 100, seed, i));
            }
            return values;
        };
        
        setWaveform(generateRandomWaveform());
    }, [audioId, waveform.length]);
    
    // Efecto para manejar el caché y la carga del audio
    useEffect(() => {
        // Si ya está en caché, no hacemos nada
        if (globalAudioCache.has(media_url)) {
            setLoading(false);
            return;
        }
        
        // Verificar si la URL cambió
        if (originalUrlRef.current !== media_url) {
            console.warn('La URL del audio cambió para el mismo mensaje:', audioId);
            originalUrlRef.current = media_url;
        }
        
        // Para casos donde media_url es undefined o null
        if (!media_url) {
            setLoading(false);
            return;
        }
        
        // Marcar como "en proceso de carga"
        globalAudioCache.set(media_url, "loading");
        
        // Intentar precargar el audio
        const audio = new Audio();
        
        audio.onloadedmetadata = () => {
            globalAudioCache.set(media_url, "loaded");
            setLoading(false);
            if (!initialDuration && audio.duration) {
                setDuration(audio.duration);
            }
        };
        
        audio.onerror = () => {
            globalAudioCache.set(media_url, "error");
            setLoading(false);
        };
        
        audio.src = media_url;
        
        return () => {
            audio.onloadedmetadata = null;
            audio.onerror = null;
        };
    }, [media_url, audioId, initialDuration]);
    
    // Efecto para detectar actualizaciones de estado desde Ably
    useEffect(() => {
        if (status && globalAudioCache.has(media_url)) {
            setLoading(false);
        }
    }, [status, media_url]);
    
    // Formatear tiempo en minutos:segundos
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? '0' + secs : secs}`;
    };
    
    // Manejadores de reproducción
    const handlePlayPause = () => {
        if (!audioRef.current) return;
        
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            // Asegurarse de que el audio esté cargado antes de reproducir
            const playPromise = audioRef.current.play();
            
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.error("Error al reproducir el audio:", error);
                    setIsPlaying(false);
                });
            }
        }
        setIsPlaying(!isPlaying);
    };
    
    const handleTimeUpdate = () => {
        if (!audioRef.current) return;
        setCurrentTime(audioRef.current.currentTime);
    };
    
    const handleLoadedMetadata = () => {
        if (!audioRef.current) return;
        
        globalAudioCache.set(media_url, "loaded");
        setLoading(false);
        setDuration(audioRef.current.duration);
    };
    
    const handleSliderChange = (event, newValue) => {
        if (!audioRef.current) return;
        
        audioRef.current.currentTime = newValue;
        setCurrentTime(newValue);
    };
    
    const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
        }
    };
    
    // Calcular qué porcentaje del waveform debe estar "activo" basado en el progreso
    const activePercentage = (currentTime / (duration || 1)) * 100;
    
    return (
        <MessageBase sender={sender} timestamp={timestamp} status={status}>
            <Box sx={{ paddingBottom: '15px', width: '100%' }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    width: '100%',
                    gap: 1,
                    opacity: loading ? 0.6 : 1,
                    transition: 'opacity 0.3s'
                }}>
                    {/* Icono de micrófono y botón de reproducción */}
                    <IconButton 
                        onClick={handlePlayPause} 
                        disabled={loading}
                        sx={{ 
                            color: sender === 'user' ? 'white' : 'primary.main',
                            backgroundColor: sender === 'user' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                            position: 'relative',
                            cursor: 'pointer',
                            '&.Mui-disabled': {
                                opacity: 0.5,
                                color: sender === 'user' ? 'white' : 'primary.main'
                            }
                        }}
                    >
                        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                        <MicIcon 
                            sx={{ 
                                position: 'absolute', 
                                fontSize: '10px',
                                bottom: '2px',
                                right: '2px',
                                color: sender === 'user' ? 'white' : 'primary.main'
                            }} 
                        />
                    </IconButton>
                    
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
                        {/* Visualización de waveform */}
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                height: '30px',
                                gap: '2px',
                                mb: 0.5,
                                width: '100%'
                            }}
                        >
                            {waveform.map((height, index) => (
                                <Box 
                                    key={index}
                                    sx={{
                                        height: `${height}%`,
                                        width: '2px',
                                        backgroundColor: index / waveform.length * 100 <= activePercentage 
                                            ? sender === 'user' ? 'white' : 'primary.main'
                                            : sender === 'user' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.2)',
                                        borderRadius: '1px',
                                        flex: 1
                                    }}
                                />
                            ))}
                        </Box>
                        
                        {/* Slider para control de reproducción (invisible pero funcional) */}
                        <Slider
                            size="small"
                            value={currentTime}
                            max={duration || 100}
                            onChange={handleSliderChange}
                            disabled={loading}
                            sx={{ 
                                color: sender === 'user' ? 'white' : 'primary.main',
                                height: 0,
                                padding: 0,
                                '& .MuiSlider-thumb': {
                                    display: 'none'
                                }
                            }}
                        />
                        
                        {/* Duración del audio */}
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            fontSize: '0.7rem',
                            color: sender === 'user' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                        }}>
                            <Typography variant="caption">
                                {formatTime(currentTime)}
                            </Typography>
                            <Typography variant="caption">
                                {formatTime(duration)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                
                {/* Audio element (hidden) */}
                {media_url && (
                    <audio 
                        ref={audioRef}
                        src={media_url}
                        onTimeUpdate={handleTimeUpdate}
                        onLoadedMetadata={handleLoadedMetadata}
                        onEnded={handleEnded}
                    />
                )}
            </Box>
        </MessageBase>
    );
};

export default React.memo(VoiceMessage);